/**
 * For Businesses (slot model; mounted at /for-businesses when SLOT_MODEL is on —
 * pages/ForBusinesses.jsx stays for flag-off). Audience: local businesses buying placements.
 *
 * Sept 2026 redesign: ports site/for-businesses/index.html of the approved package (hero with
 * BIZ-HERO-01 and the printed-coaster card since U4, the category mosaic, the report photo, benefit strip, "A small format", the monthly report, FAQ,
 * "Pull up a chair" call-out) onto components/site. Copy is the package's, with the copy
 * audit's corrections (investigations/04-copy-audit.md): "From scan to redemption", "Scans &
 * page visits", "Placement records from each venue". No figures on this page: prices, coaster
 * quantities (per month) and the multi-venue discount live on /pricing, availability on
 * /venues. The sales path while payments are off is the enquiry dialog (EnquiryButton):
 * "Check availability" in the hero and the closing band (CTA vocabulary, upgrade brief §2.2).
 */
import React from 'react';
import { Gift, MapPin, Sparkle } from 'lucide-react';
import SEO from '../../components/SEO';
import { Button, CtaBand, Eyebrow, Heading, PageHero, SiteSection } from '@/components/site';
import { EnquiryButton } from '@/components/site/enquiry';
import { PlansFromLine, PricingTeaser } from '@/components/site/pricing';
import {
  AdvertiserVisual, AudienceFaq, AudienceMain, BAND_TINT, BenefitStrip, MetricList, PAGE_BUTTON, PaperPanel, PhotoMosaic,
  ReportPhoto, SectionTop,
} from './audienceParts';

const BENEFITS = [
  {
    icon: MapPin,
    eyebrow: 'A place to be seen',
    title: 'At local tables.',
    text: 'Bars and restaurants in the neighborhoods you want to reach.',
  },
  {
    icon: Gift,
    eyebrow: 'Room for your business',
    title: 'Your category. Your spot.',
    text: 'One paid advertiser per category, per venue.',
  },
  {
    icon: Sparkle,
    eyebrow: 'A way to follow through',
    title: 'From scan to redemption.',
    text: 'A digital offer and reporting on recorded guest activity.',
  },
];

const REPORT = [
  { name: 'Coasters placed', detail: 'Placement records from each venue' },
  { name: 'Scans & page visits', detail: 'Recorded interactions' },
  { name: 'Claims & leads', detail: 'Guest actions' },
  { name: 'Redemptions', detail: 'Recorded code uses' },
];

const FAQ = [
  {
    q: 'Can a competitor be on the same venue’s coasters?',
    a: 'There is one paid advertiser per category, per venue. Confirm your category and availability before you commit.',
  },
  {
    q: 'When does my creative reach the tables?',
    a: 'Printing follows quarterly runs. The team confirms your next print date. Your digital listing and offer can be updated separately.',
  },
  {
    q: 'Can I start without paying?',
    a: 'Yes. A free listing includes one digital offer at up to three selected venues. It does not include printed placement.',
  },
  {
    q: 'What is the minimum commitment?',
    a: 'Standard and Premium have a three-month minimum with quarterly billing, or an annual prepaid option. Venue Sponsor has a 12-month commitment billed quarterly.',
  },
];

export default function ForBusinessesSlot() {
  return (
    <AudienceMain>
      <SEO
        title="For Businesses"
        description="Put your business on the table at local bars and restaurants: printed coasters, a digital offer and reporting. One paid advertiser per category per venue."
        url="/for-businesses"
        image="/images/og/for-businesses.jpg"
      />

      <PageHero
        eyebrow="For local businesses"
        title={(
          <>
            Your next customer
            {' '}
            <br />
            is already
            {' '}
            <br />
            <em>out there.</em>
          </>
        )}
        lede="Put your offer on the coasters at neighborhood bars and restaurants. One business per category, per venue. Guests scan, claim, and come find you."
        actions={(
          <>
            <EnquiryButton className={PAGE_BUTTON} role="advertiser">Check availability</EnquiryButton>
            <Button className={PAGE_BUTTON} to="/pricing" variant="secondary">See pricing</Button>
          </>
        )}
        fine={<PlansFromLine />}
        media={<AdvertiserVisual />}
      />

      <BenefitStrip label="Why advertise with Tappd" items={BENEFITS} />

      <SiteSection divider aria-labelledby="fb-placements-title">
        <SectionTop
          eyebrow="Your place in the neighborhood"
          titleId="fb-placements-title"
          title={(
            <>
              A small format.
              {' '}
              <br />
              A proper introduction.
            </>
          )}
          intro="Choose how your business shows up. Every paid placement is sold per venue, subject to category availability."
        />
        {/* Upgrade brief §3.3 item 4: the tier's coaster photo, "from" prices, a "Best for" line
            and "Choose {Tier}" per tier. */}
        <PricingTeaser bestFor />
        <div className="mt-[30px] flex flex-col items-stretch gap-3.5 sm:flex-row sm:items-center">
          <Button className={PAGE_BUTTON} to="/pricing" variant="secondary">See pricing</Button>
        </div>
      </SiteSection>

      {/* Upgrade brief §3.3 item 5: the six-photo mosaic (was words inside the hero card). */}
      <SiteSection divider aria-labelledby="fb-categories-title">
        <SectionTop
          eyebrow="Made for your neighborhood"
          titleId="fb-categories-title"
          title={(
            <>
              Realtors. Salons. Gyms.
              {' '}
              <br />
              Shops. Services. You.
            </>
          )}
          intro="One business per category at each venue, so you’re never on the same coaster as your competitor."
        />
        <PhotoMosaic label="Local businesses on Tappd coasters, by category" />
      </SiteSection>

      <SiteSection
        divider
        className={BAND_TINT}
        aria-labelledby="fb-report-title"
        containerClassName="grid grid-cols-1 items-center gap-[35px] nav:grid-cols-2 min-[1051px]:gap-[70px]"
      >
        <div className="min-w-0">
          <Eyebrow>Less guesswork</Eyebrow>
          <Heading id="fb-report-title" className="mb-5 mt-5">
            Know what went out.
            {' '}
            <br />
            See what came back.
          </Heading>
          <p className="max-w-[66ch] break-words leading-[1.55] text-brand-muted">
            Follow the path from printed coasters to recorded activity. A scan shows interest; a recorded
            redemption shows a code was used. Neither is a promise of revenue.
          </p>
          <ReportPhoto className="mt-8" sizes="(min-width: 1808px) 787px, (min-width: 800px) 48vw, 100vw" />
        </div>
        <PaperPanel>
          <Eyebrow as="p">Your monthly report</Eyebrow>
          <MetricList items={REPORT} />
        </PaperPanel>
      </SiteSection>

      <AudienceFaq
        id="fb-faq"
        titleId="fb-faq-title"
        title={(
          <>
            A few good
            {' '}
            <br />
            questions.
          </>
        )}
        items={FAQ}
      />

      <CtaBand
        tone="peach"
        eyebrow="There’s a place for you here"
        title="Pull up a chair."
        text="Tell us the neighborhood you want to be part of."
        actions={(
          <>
            <EnquiryButton className={PAGE_BUTTON} role="advertiser">Check availability</EnquiryButton>
            <Button className={PAGE_BUTTON} to="/free-listing" variant="secondary">Start with a free listing</Button>
          </>
        )}
      />
    </AudienceMain>
  );
}
